import { COLORS } from '../../constants/theme';

export interface RangePreset {
  label: string;
  lower: number | null;
  upper: number | null;
}

interface RangeControlProps {
  refValue: number | null;
  refLabel?: string;
  presets: RangePreset[];
  activePreset: number;
  onPresetSelect: (index: number) => void;
  lowerVal: string;
  upperVal: string;
  onLower: (val: string) => void;
  onUpper: (val: string) => void;
  unit?: string;
  /**
   * SF-171: additive variant, default `'compact'` — preserves today's byte-for-byte rendering for
   * every existing (desktop) caller, none of which pass this prop. `'touch'` bumps preset buttons
   * to a 44px tap height, raises input font-size to 16px (avoids iOS auto-zoom-on-focus), and adds
   * an `aria-label` per Min/Max input instead of relying on `placeholder` alone (placeholder text
   * disappears once a value is typed) — closes a real mobile-ergonomics gap flagged independently
   * by two Phase 2 reviewers, without forking a parallel `MobileRangeControl.tsx` (a second source
   * of truth for the same preset/min-max-input semantics).
   */
  size?: 'compact' | 'touch';
}

export function RangeControl({
  refValue, refLabel, presets, activePreset, onPresetSelect,
  lowerVal, upperVal, onLower, onUpper, unit, size = 'compact',
}: RangeControlProps) {
  const isTouch = size === 'touch';

  return (
    <div>
      {/* Reference value */}
      <div style={{ marginBottom: 4, textAlign: 'right' }}>
        <span style={{ color: COLORS.text.muted, fontSize: 10, fontFamily: "'JetBrains Mono', monospace" }}>
          yours: {refValue != null ? refValue.toLocaleString() : '---'}
          {refLabel && refValue != null ? ` ${refLabel}` : ''}
        </span>
      </div>

      {/* Preset buttons */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 6 }}>
        {presets.map((p, i) => (
          <button
            key={i}
            type="button"
            onClick={() => onPresetSelect(i)}
            style={{
              flex: 1,
              padding: '3px 0',
              minHeight: isTouch ? 44 : undefined,
              borderRadius: 3,
              fontSize: 10,
              fontWeight: 600,
              cursor: 'pointer',
              background: activePreset === i ? COLORS.link : 'transparent',
              color: activePreset === i ? '#fff' : COLORS.text.tertiary,
              border: `1px solid ${activePreset === i ? COLORS.link : COLORS.border}`,
            }}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Min/Max inputs */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {renderInput('Min', 'Minimum', lowerVal, onLower, isTouch)}
        <span style={{ color: COLORS.text.muted, fontSize: 11 }}>&ndash;</span>
        {renderInput('Max', 'Maximum', upperVal, onUpper, isTouch)}
        {unit && <span style={{ color: COLORS.text.muted, fontSize: 10, minWidth: 14 }}>{unit}</span>}
      </div>
    </div>
  );
}

function renderInput(
  placeholder: 'Min' | 'Max',
  a11yLabel: 'Minimum' | 'Maximum',
  value: string,
  onChange: (val: string) => void,
  isTouch: boolean
) {
  // 'touch' adds a real accessible name (aria-label) instead of relying on placeholder alone —
  // placeholder text disappears once a value is typed and screen readers treat it inconsistently.
  return (
    <input
      type="number"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      aria-label={isTouch ? a11yLabel : undefined}
      style={{
        flex: 1,
        padding: '5px 8px',
        borderRadius: 4,
        background: COLORS.inputBg,
        border: `1px solid ${COLORS.inputBorder}`,
        color: COLORS.text.primary,
        fontSize: isTouch ? 16 : 12,
        fontFamily: "'JetBrains Mono', monospace",
        textAlign: 'center',
      }}
    />
  );
}
